import React, { useState, useReducer, useEffect, useCallback, useRef } from 'react'
import { Button, Classes, H5, InputGroup } from '@blueprintjs/core'
import { IconButton, NewGridTable } from '.'
import { IGridTableColumn, GridTableStatus } from './NewGridTable'
import { IPageInfo, IConnectionWithTotal, Order } from '@api/types'
import { debounce } from 'lodash'
import { prettyNumber } from './PrettyNumber'
import { pluralize } from '@utils'
import FAIcon from './FAIcon'
import { RootEvent } from '@track'
import Event from '@track/Event'

export type ListFuc<T> = ({
  query,
  after,
}: {
  query: string
  after?: string
}) => Promise<IConnectionWithTotal<T>>

export interface MiniManagementTrack {
  track: (event: RootEvent) => void
  component: string
}

interface MinimManagementSectionProps<T> {
  title: string
  list: ListFuc<T>
  gridTableProps: {
    columns: IGridTableColumn<T>[]
    header?: boolean
    sticky?: boolean
    infiniteScroll?: boolean
    noDataMessage?: string
  }
  trackProps?: MiniManagementTrack
}

export default function MiniManagementSection<T extends { id: string }>({
  title,
  list,
  gridTableProps,
  trackProps,
}: MinimManagementSectionProps<T>) {
  const [showSearch, setShowSearch] = useState(false)
  const hook = useTableData<T>(list, trackProps)

  return (
    <div>
      <div className="h-8 flex items-center">
        <H5 style={{ margin: 0 }}>{title}</H5>
        <div className="ml-2">
          {showSearch ? (
            <InputGroup
              autoFocus
              placeholder="Search..."
              leftIcon={<FAIcon name="search" />}
              value={hook.query}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                hook.search(e.target.value)
              }}
              rightElement={
                <IconButton
                  minimal
                  iconName="times"
                  onClick={() => {
                    if (hook.query) hook.search('')
                    setShowSearch(false)
                    if (trackProps)
                      trackProps.track({
                        eventName: Event.CLEAR_FILTER,
                        props: {
                          component: trackProps.component,
                          filterName: 'Query',
                          filterValue: null,
                        },
                      })
                  }}
                />
              }
            />
          ) : (
            <IconButton
              round
              minimal
              iconName="search"
              tooltip="Search"
              onClick={() => setShowSearch(true)}
            />
          )}
        </div>
      </div>
      <div className="flex items-center justify-between h-12 text-sm text-grey-600">
        <RecordCount filtered={!!hook.query} status={hook.status} count={hook.counters.total} />
      </div>
      <div className="border border-grey-200 rounded overflow-hidden" style={{ minHeight: 196 }}>
        <NewGridTable<T>
          noDataMessage={gridTableProps.noDataMessage}
          infiniteScroll={gridTableProps.infiniteScroll ?? false}
          header={gridTableProps.header ?? true}
          sticky={gridTableProps.sticky ?? false}
          skeletonSize={
            hook.status === 'paging'
              ? Math.min(5, hook.counters.total - hook.results.length)
              : hook.results.length || 5
          }
          status={hook.status}
          columns={gridTableProps.columns}
          data={hook.results.map((data) => ({ data }))}
          sort={{
            column: 'name',
            order: Order.ASC,
            onSort: () => null,
          }}
        />
      </div>
      {hook.pageInfo.hasNextPage && (
        <div className="mt-2">
          <Button
            minimal
            text={`View ${Math.min(5, hook.counters.total - hook.results.length)} more`}
            onClick={hook.next}
          />
        </div>
      )}
    </div>
  )
}

function RecordCount({
  count,
  status,
  filtered,
}: {
  count: number
  status: GridTableStatus
  filtered: boolean
}) {
  if (status === 'loading' || status === 'filtering') {
    return <span className={Classes.SKELETON}>placeholder</span>
  }

  return (
    <span>
      {`${prettyNumber({ value: count })}${filtered ? ' filtered' : ''} ${pluralize(
        count,
        'record'
      )}`}
    </span>
  )
}

// Table hook
interface SearchAction {
  type: 'search'
  query: string
}

interface LoadAction<T> {
  type: 'load'
  counters: { total: number }
  pageInfo: IPageInfo
  results: T[]
}

interface PageAction {
  type: 'page'
}

type Action<T> = SearchAction | LoadAction<T> | PageAction

interface State<T> {
  status: GridTableStatus
  query: string
  after: string
  counters: { total: number }
  pageInfo: IPageInfo
  results: T[]
}

interface Hook<T> extends State<T> {
  setActiveItem: (acitveItem: T | null) => void
  search: (query: string) => void
  next: () => void
}

export function useTableData<T extends { id: string }>(
  list: ListFuc<T>,
  trackprops?: MiniManagementTrack
): Hook<T> {
  const reqId = useRef(0)
  const [state, dispatch]: [State<T>, React.Dispatch<Action<T>>] = useReducer(reducer, {
    status: 'loading',
    query: '',
    after: '',
    counters: { total: 0 },
    pageInfo: {
      hasNextPage: false,
      endCursor: '',
    },
    results: [] as T[],
  } as never)

  const debouncedList = useCallback(
    debounce((params: { query: string; after: string }, newReqId: number) => {
      list(params).then(({ pageInfo, edges, counters }) => {
        if (reqId.current === newReqId) {
          const newResults = edges.map(({ node }) => node)
          dispatch({
            type: 'load',
            pageInfo,
            counters,
            results: state.after ? [...state.results, ...newResults] : newResults,
          })
        }
      })
    }, 300),
    []
  )

  useEffect(() => {
    const newReqId = Date.now()
    reqId.current = newReqId
    debouncedList({ query: state.query, after: state.after }, newReqId)
  }, [state.query, state.after, debouncedList])

  return {
    ...state,
    search(query: string) {
      dispatch({ type: 'search', query })
    },
    next() {
      if (state.pageInfo.hasNextPage && state.status === 'idle') {
        dispatch({ type: 'page' })
        if (trackprops)
          trackprops.track({
            eventName: Event.PAGE,
            props: {
              component: trackprops.component,
            },
          })
      }
    },
    setActiveItem(activeItem: T | null) {
      /* eslint-disable @typescript-eslint/ban-ts-comment */
      // @ts-ignore
      dispatch({ type: 'activeItemUpdate', activeItem })
    },
  }
}

function reducer<T>(state: State<T>, action: Action<T>) {
  switch (action.type) {
    case 'search':
      return {
        ...state,
        query: action.query,
        after: '',
        status: 'filtering',
      }

    case 'load':
      return {
        ...state,
        status: 'idle',
        counters: action.counters,
        pageInfo: action.pageInfo,
        results: state.after ? [...state.results, ...action.results] : action.results,
      }

    case 'page':
      return {
        ...state,
        status: 'paging',
        after: state.pageInfo.endCursor,
      }

    default:
      return state
  }
}
